import { useEffect, useMemo } from 'react'
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { FaFacebookF, FaInstagram, FaWhatsapp } from 'react-icons/fa'
import { FaTwitter } from 'react-icons/fa6'

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

import PhoneInput from '@/components/forms/PhoneInput'
import SingleRadio from '@/components/forms/SingleRadio'
import { updateBusinessDetails } from '@/store/slices/initSlice'

import useServerBusiness from '../../api/useServerBusiness'
import useServer from '../../api/useServerMain'
import AsyncComboBox from '../../components/forms/AsyncCombobox'
import Input from '../../components/forms/Input'
import TextArea from '../../components/forms/TextArea'
import Button from '../../components/misc/Button'
import Fetching from '../../components/misc/Fetching'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { cleanObj } from '../../utils/helpers'

const ContactConfig = () => {
  const { denyRoles } = useServer()

  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'phones',
  })

  const {
    fields: socialNet,
    append: appendSocialNet,
    remove: removeSocialNet,
    replace: replaceSocialNet,
  } = useFieldArray({
    control,
    name: 'socialNetworks',
  })

  const { business, businessDetails } = useAppSelector((state) => state.init)

  const { editBusiness, isFetching } = useServerBusiness()
  type ContactFormValues = Record<
    string,
    | string
    | unknown[]
    | number
    | boolean
    | { id: string; type: string }
    | {
        number: string
        description: string
        codeCountry: string | number
        isMain: boolean
      }[]
  >

  const onSubmit: SubmitHandler<ContactFormValues> = (data) => {
    data.address = cleanObj(data.address)

    const mainIdx = Number(getValues('mainPhoneIndex'))

    const transformedPhones = (data.phones as Record<string, unknown>[]).map(
      (phone, idx) => {
        const phoneNumber = phone.number as Record<string, string>
        return {
          number: `+${phoneNumber.countryCode}${phoneNumber.phoneNumber}`,
          description: phone.description as string,
          codeCountry: phone.codeCountry ?? null,
          isMain: idx === mainIdx,
        }
      }
    )

    data.phones = transformedPhones
    delete data.mainPhoneIndex
    editBusiness(cleanObj(data)).then(() => {
      dispatch(updateBusinessDetails(cleanObj(data)))
    })
    // editBusiness(cleanObj(data))

    removeSocialNet()
    remove()
  }

  useEffect(() => {
    if (businessDetails?.phones?.length) {
      replace(businessDetails.phones)
      const mainIdx = businessDetails.phones.findIndex((p) => p.isMain)
      if (mainIdx !== -1) setValue('mainPhoneIndex', mainIdx.toString())
    } else {
      replace([])
    }

    if (business?.socialNetworks?.length) {
      replaceSocialNet(business.socialNetworks)
    } else {
      replaceSocialNet([])
    }
  }, [businessDetails, business, replace, replaceSocialNet, setValue])

  const countryIdValue = watch('address.countryId')
  const provinceIdValue = watch('address.provinceId')
  const municipalityIdValue = watch('address.municipalityId')

  useEffect(() => {
    const countryId = getValues('address.countryId')
    const provinceId = getValues('address.provinceId')
    const municipalityId = getValues('address.municipalityId')
    if (countryId === undefined) setValue('address.countryId', null)
    if (provinceId === undefined) setValue('address.provinceId', null)
    if (municipalityId === undefined) setValue('address.municipalityId', null)
    if (!provinceId && municipalityId) setValue('address.municipalityId', null)
  }, [
    getValues,
    setValue,
    countryIdValue,
    provinceIdValue,
    municipalityIdValue,
  ])

  //manage SocialNetworks form --------------------------------------------------------------------------
  const socialNetworksTypes = [
    {
      type: 'WHATSAPP',
      icon: <FaWhatsapp className="text-2xl text-green-600" />,
      color: 'green-700',
      link: 'https://wa.me/',
    },
    {
      type: 'FACEBOOK',
      icon: <FaFacebookF className="text-2xl text-blue-800" />,
      color: 'blue-500',
      link: 'https://www.facebook.com/',
    },
    {
      type: 'TWITTER',
      icon: <FaTwitter className="text-2xl text-black" />,
      color: 'blue-500',
      link: 'https://twitter.com/',
    },
    {
      type: 'INSTAGRAM',
      icon: (
        <FaInstagram
          className={[
            'rounded-lg bg-gradient-to-tr from-pink-500 via-purple-500',
            'to-indigo-500 text-2xl text-white',
          ].join(' ')}
        />
      ),
      color: 'gray-500',
      link: 'https://www.instagram.com/',
    },
  ]

  //--------------------------------------------------------------------------------
  //Default address props ---------------------------------------------
  const defaultAddress: Partial<{
    countryId: number
    defaultCountry: { id: number; name: string }
    provinceId: number
    defaultProvince: { id: number; name: string }
    defaultMunicipality: { id: number; name: string }
  }> = useMemo(() => {
    const address = business?.address
    return {
      countryId: address?.country?.id,
      defaultCountry: address?.country
        ? { id: address.country.id, name: address.country.name }
        : undefined,
      provinceId: address?.province?.id,
      defaultProvince: address?.province
        ? { id: address.province.id, name: address.province.name }
        : undefined,
      defaultMunicipality: address?.municipality
        ? {
            id: address.municipality.id,
            name: address.municipality.name,
          }
        : undefined,
    }
  }, [business])

  // debug: fields
  // console.log('fields!!!!!!!!!!!!', fields)

  //--------------------------------------------------------------------------------
  if (isFetching) return <Fetching />
  return (
    <form
      className="m-auto rounded-lg bg-gray-50 p-5 shadow-md"
      onSubmit={handleSubmit(onSubmit)} /* w-3/4 */
    >
      <div className="md:grid md:grid-cols-2 md:gap-2">
        <div className="col-span-2 pt-8">
          <div className="relative flex justify-center border-t border-slate-500">
            <span className="relative -top-3 bg-gray-50 px-5 text-center">
              Dirección
            </span>
          </div>
        </div>
        <Input
          control={control}
          defaultValue={business?.address?.street_1}
          label="Calle principal"
          name="address.street_1"
        />
        <Input
          control={control}
          defaultValue={business?.address?.street_2}
          label="Calle secundaria"
          name="address.street_2"
        />
        <Input
          control={control}
          defaultValue={business?.address?.city}
          label="Localidad"
          name="address.city"
        />
        <AsyncComboBox
          control={control}
          dataQuery={{ url: '/public/countries' }}
          defaultItem={defaultAddress?.defaultCountry}
          label="País"
          name="address.countryId"
          normalizeData={{ id: 'id', name: 'name' }}
          nullOpt={{ id: null, name: '' }}
        />
        <AsyncComboBox
          control={control}
          dataQuery={{
            url: '/public/provinces',
            defaultParams: defaultAddress.countryId
              ? { countryId: defaultAddress.countryId }
              : undefined,
          }}
          defaultItem={defaultAddress?.defaultProvince}
          dependendValue={{ countryId: watch('address.countryId') }}
          label="Provincia"
          name="address.provinceId"
          normalizeData={{ id: 'id', name: 'name' }}
          nullOpt={{ id: null, name: '' }}
        />
        <AsyncComboBox
          control={control}
          dataQuery={{
            url: '/public/municipalities',
            defaultParams: defaultAddress?.provinceId
              ? { provinceId: defaultAddress.provinceId }
              : undefined,
          }}
          defaultItem={defaultAddress?.defaultMunicipality}
          dependendValue={{ provinceId: watch('address.provinceId') }}
          label="Municipio"
          name="address.municipalityId"
          normalizeData={{ id: 'id', name: 'name' }}
          nullOpt={{ id: null, name: '' }}
        />

        <div className="col-span-2">
          <TextArea
            control={control}
            defaultValue={business?.address?.description}
            label="Observaciones"
            name="address.description"
          />
        </div>
      </div>
      {/**Teléfonos */}
      <div className="pt-8">
        <div className="relative flex justify-center border-t border-slate-500">
          <span className="relative -top-3 bg-gray-50 px-5 text-center">
            Teléfono
          </span>
        </div>
      </div>
      <Controller
        control={control}
        name="mainPhoneIndex"
        render={({ field }) => <input type="hidden" {...field} />}
        rules={{
          required: 'Debe seleccionar un teléfono principal',
        }}
      />

      {errors.mainPhoneIndex && (
        <p className="mt-2 text-sm text-red-500">
          {errors.mainPhoneIndex.message?.toString()}
        </p>
      )}

      {fields.map((item, idx) => (
        <div
          className="mt-5 flex w-full flex-col items-start gap-2 md:flex-row md:items-end"
          key={item.id}
        >
          <div className="mb-3 flex w-full flex-col items-center justify-center md:w-[10%]">
            <label className="mb-[15px] text-center text-sm font-medium leading-tight">
              Principal
            </label>
            <SingleRadio
              checked={getValues('mainPhoneIndex') === idx.toString()}
              control={control}
              label=""
              name="mainPhoneIndex"
              onChangeFunction={() => {
                setValue('mainPhoneIndex', idx.toString(), {
                  shouldDirty: true,
                })
                fields.forEach((_, i) => {
                  setValue(`phones.${i}.isMain`, i === idx, {
                    shouldDirty: true,
                  })
                })
              }}
              value={idx.toString()}
            />
          </div>
          <div className="flex w-full flex-col gap-2 md:flex-row">
            <div className="w-full md:w-[50%]">
              <PhoneInput
                control={control}
                label="Teléfono *"
                name={`phones.${idx}.number`}
                placeholder={'Teléfono'}
                rules={{
                  validate: (value) => {
                    if (!value || isNaN(value.phoneNumber)) {
                      return 'Llene los datos o elimine el campo'
                    }

                    const numberStr = String(value.phoneNumber).replace(
                      /\D/g,
                      ''
                    )
                    if (numberStr.length < 8) {
                      return 'El número es demasiado corto'
                    }

                    if (numberStr.length > 12) {
                      return 'El número es demasiado largo'
                    }

                    return true
                  },
                }}
                valueDefault={
                  business?.phones?.[idx]?.number
                    ? {
                        phoneNumber: Number(
                          business.phones[idx].number.slice(3)
                        ),
                        countryCode: Number(
                          business.phones[idx].number.slice(0, 3)
                        ),
                      }
                    : undefined
                }
              />
            </div>

            <div className="w-full md:w-[45%]">
              <Input
                control={control}
                defaultValue={business?.phones[idx]?.description}
                label="Descripción"
                name={`phones.${idx}.description`}
                rules={{ required: 'Llene los datos o elimine el campo' }}
              />
            </div>
          </div>

          <div className="pt-2 md:pt-5">
            <Button
              action={() => remove(idx)}
              color="red-500"
              icon={<TrashIcon className="h-5" />}
            />
          </div>
        </div>
      ))}

      <div className="mt-5 flex justify-end gap-2">
        <Button
          action={() => append({})}
          color="slate-400"
          full
          icon={<PlusIcon className="h-5" />}
          name="Insertar"
          outline
          textColor="slate-500"
        />
      </div>

      {/**Correo electrónico */}
      <div className="pt-8">
        <div className="relative flex justify-center border-t border-slate-500">
          <span className="relative -top-3 bg-gray-50 px-5 text-center">
            Correo Electrónico
          </span>
        </div>
      </div>

      <Input
        control={control}
        defaultValue={business?.email}
        label="Correo Electrónico"
        name="email"
      />

      {/**Redes Sociales */}
      <div className="pt-8">
        <div className="relative flex justify-center border-t border-slate-500">
          <span className="relative -top-3 bg-gray-50 px-5 text-center">
            Redes Sociales
          </span>
        </div>
      </div>

      <div className="inline-flex w-full justify-center gap-3">
        {socialNetworksTypes.map((item, idx) => (
          <div key={idx}>
            <Button
              action={() =>
                !getValues('socialNetworks')?.find(
                  (value: { type: string }) => item.type === value.type
                ) && appendSocialNet({ type: item.type })
              }
              color={item.color}
              icon={item.icon}
              outline
            />
          </div>
        ))}
      </div>

      <div className="flex w-full justify-center">
        <div className="flex flex-col">
          {socialNet.map((item, idx) => (
            <div className="inline-flex justify-between gap-2" key={item.id}>
              <div className="flex items-center gap-2" key={item.id}>
                {
                  socialNetworksTypes.find(
                    (itm) => itm.type === getValues('socialNetworks')[idx].type
                  )?.icon
                }
                <Input
                  control={control}
                  defaultValue={business?.socialNetworks[idx]?.user}
                  name={`socialNetworks.${idx}.user`}
                  placeholder="Usuario"
                  rules={{ required: 'Llene los datos o elimine el campo' }}
                  stackedText={
                    socialNetworksTypes.find(
                      (itm) =>
                        itm.type === getValues('socialNetworks')[idx].type
                    )?.link
                  }
                />
              </div>
              <div className="justify-cente flex items-end py-2">
                <Button
                  action={() => removeSocialNet(idx)}
                  color="red-500"
                  icon={<TrashIcon className="h-5" />}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end py-5">
        <Button
          color="slate-600"
          disabled={!denyRoles(['AUDITOR'])}
          name="Actualizar"
          type="submit"
        />
      </div>
    </form>
  )
}

export default ContactConfig
